import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { withRouter } from '../../common/withRouter';
import { Notification, Message } from '../../web.common';
import PageTop from '../layout/PageTop';
import withApi from '../higherOrderComponents/withApi';
import { fmtUtc, fmtMoney, StatusBadge, tableWrap, table, headRow, bodyRow, cell, muted } from './playGameFormat';

// Everything about one PLAY game: who is on each side, who was invited and
// how far each invite got, the money (checkout orders + charges/refunds, with
// Frisbii deep links) and the event timeline. Read-only.
const PlayGameInternal = (props) => {
  const code = props.match?.params?.code || props.params?.code;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await props.api.playGames.get(code));
    } catch (e) {
      setNotification(new Message('error', `Failed to load: ${e?.message || 'unknown'}`));
    } finally {
      setLoading(false);
    }
  }, [props.api, code]);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <div style={{ padding: 16 }}>Loading…</div>;
  if (!data?.game) return <div style={{ padding: 16 }}>Game not found. <Link to='/play/games'>Back to games</Link></div>;

  const { game, sides = [], invites = [], orders = [], transactions = [], timeline = [], users = {} } = data;
  const cur = game.currencyIsoCode;

  // A user as name (linked to the admin user page) + email, from the users map.
  const user = (id) => {
    if (!id) return <span>—</span>;
    const u = users[id];
    return (
      <span>
        <Link to={`/user/${id}`}>{u?.name || id}</Link>
        {u?.email && <div style={muted}>{u.email}</div>}
      </span>
    );
  };

  const criteria = game.criteria || {};
  const criteriaText = [
    criteria.criteriaLevel != null && `level ${criteria.criteriaLevel}`,
    (criteria.criteriaAgeMin != null || criteria.criteriaAgeMax != null) &&
      `age ${criteria.criteriaAgeMin ?? '…'}–${criteria.criteriaAgeMax ?? '…'}`,
    criteria.gender && criteria.gender,
  ].filter(Boolean).join(' · ') || 'none';

  return (
    <>
      <Notification newMessage={notification} />
      <PageTop documentTitle={`Play game ${game.code}`} documentDescription='Play game detail' mainHeadingContent={`Play game ${game.code}`} />
      <div style={{ padding: 16, color: '#444', maxWidth: 1200 }}>
        <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between' }}>
          <Link to='/play/games' style={{ color: '#666', fontSize: 13 }}>← Back to games</Link>
          <button type='button' onClick={load} disabled={loading} style={{ padding: '4px 10px' }}>
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>

        <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8, padding: 16, marginBottom: 16 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <StatusBadge status={game.status} />
            <span style={muted}>
              {game.sport} · {game.format} · {game.gameType} · {game.origin} · lang {game.language}
            </span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 16, marginTop: 16 }}>
            <Field label='Starts'>{fmtUtc(game.startsAt)}<div style={muted}>ends {fmtUtc(game.endsAt)}</div></Field>
            <Field label='Where'>
              {game.facilityId ? <Link to={`/facility/${game.facilityId}`}>{game.facilityName || game.facilityId}</Link> : '—'}
              {game.courtName && <div style={muted}>{game.courtName}</div>}
              {game.activityId && <div style={muted}><Link to={`/activity/${game.activityId}`}>activity</Link></div>}
            </Field>
            <Field label='Created by'>{user(game.creatorUserId)}</Field>
            <Field label='Price'>
              {fmtMoney(game.totalPriceMinor, cur)}
              <div style={muted}>{fmtMoney(game.shareAmountMinor, cur)} per side</div>
            </Field>
            <Field label='Created'>{fmtUtc(game.createdAtUtc)}</Field>
            <Field label='Posting expires'>{fmtUtc(game.expiresAtUtc)}</Field>
            {game.confirmedAtUtc && <Field label='Confirmed'>{fmtUtc(game.confirmedAtUtc)}</Field>}
            {game.cancelledAtUtc && <Field label='Cancelled'>{fmtUtc(game.cancelledAtUtc)}</Field>}
            <Field label='Criteria'>{criteriaText}</Field>
            <Field label='Court booking'>
              {game.externalBookingRef || '—'}
              {game.externalBookingBy && <div style={muted}>by {game.externalBookingBy}</div>}
            </Field>
          </div>

          {game.note && (
            <div style={{ marginTop: 16, padding: 10, background: '#fafafa', borderRadius: 6, whiteSpace: 'pre-wrap' }}>
              <div style={muted}>Note from creator</div>
              {game.note}
            </div>
          )}
        </div>

        <Section title='Sides'>
          <table style={table}>
            <thead>
              <tr style={headRow}>
                <th style={cell}>#</th>
                <th style={cell}>Status</th>
                <th style={cell}>Player</th>
                <th style={cell}>Joined</th>
                <th style={cell}>Phone shared</th>
                <th style={cell}>Message / cancel</th>
              </tr>
            </thead>
            <tbody>
              {sides.map((s) => {
                const memberIds = (s.members || []).map((m) => m.userId);
                const playerIds = memberIds.length ? memberIds : [s.actorUserId].filter(Boolean);
                return (
                  <tr key={s.sideIndex} style={bodyRow}>
                    <td style={cell}>{s.sideIndex}</td>
                    <td style={cell}>
                      <StatusBadge status={s.status} />
                      {s.claimedByUserId && s.status === 'Open' && (
                        <div style={muted}>claimed by {user(s.claimedByUserId)} until {fmtUtc(s.claimedUntilUtc)}</div>
                      )}
                    </td>
                    <td style={cell}>
                      {playerIds.length ? playerIds.map((id) => <div key={id}>{user(id)}</div>) : <span style={{ color: '#bbb' }}>open seat</span>}
                    </td>
                    <td style={cell}>{fmtUtc(s.joinedAtUtc)}</td>
                    <td style={cell}>
                      {(s.members || []).length
                        ? s.members.map((m) => <div key={m.userId}>{m.phoneSharedConsent ? 'yes' : 'no'} <span style={muted}>({m.language})</span></div>)
                        : '—'}
                    </td>
                    <td style={cell}>
                      {s.message && <div style={{ whiteSpace: 'pre-wrap' }}>{s.message}</div>}
                      {s.cancelledAtUtc && (
                        <div style={{ ...muted, color: '#8a2b2b' }}>
                          cancelled {fmtUtc(s.cancelledAtUtc)}{s.cancelKind ? ` (${s.cancelKind})` : ''}{s.cancelNote ? ` — ${s.cancelNote}` : ''}
                        </div>
                      )}
                      {!s.message && !s.cancelledAtUtc && '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Section>

        <Section
          title={`Invites (${invites.length})`}
          empty={invites.length === 0 && 'No invite rows for this game. Invites sent outside the system (e.g. by hand) are not tracked here.'}
        >
          <table style={table}>
            <thead>
              <tr style={headRow}>
                <th style={cell}>Player</th>
                <th style={cell}>Status</th>
                <th style={cell}>Match score</th>
                <th style={cell}>Sent</th>
                <th style={cell}>Viewed</th>
                <th style={cell}>Skip reason</th>
              </tr>
            </thead>
            <tbody>
              {invites.map((i) => (
                <tr key={`${i.userId}-${i.createdAtUtc}`} style={bodyRow}>
                  <td style={cell}>{user(i.userId)}</td>
                  <td style={cell}><StatusBadge status={i.status} /></td>
                  <td style={cell}>{i.matchScore != null ? i.matchScore.toFixed(2) : '—'}</td>
                  <td style={cell}>{fmtUtc(i.sentAtUtc)}</td>
                  <td style={cell}>{fmtUtc(i.viewedAtUtc)}</td>
                  <td style={cell}>
                    {i.skipReason || '—'}
                    {i.skipText && <div style={muted}>{i.skipText}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>

        <Section title={`Checkout orders (${orders.length})`} empty={orders.length === 0 && 'No orders.'}>
          <table style={table}>
            <thead>
              <tr style={headRow}>
                <th style={cell}>Order</th>
                <th style={cell}>Status</th>
                <th style={cell}>Player</th>
                <th style={cell}>Amount</th>
                <th style={cell}>Created / updated</th>
                <th style={cell}>Error</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.orderHandle} style={bodyRow}>
                  <td style={cell}>
                    <code style={{ fontSize: 12 }}>{o.orderHandle}</code>
                    {o.frisbiiUrl && <div><a href={o.frisbiiUrl} target='_blank' rel='noopener noreferrer' style={{ fontSize: 12 }}>Frisbii ↗</a></div>}
                  </td>
                  <td style={cell}><StatusBadge status={o.status} /></td>
                  <td style={cell}>{user(o.userId)}</td>
                  <td style={cell}>{fmtMoney(o.amountMinor, o.currencyIsoCode)}</td>
                  <td style={cell}>{fmtUtc(o.createdAtUtc)}<div style={muted}>{fmtUtc(o.updatedAtUtc)}</div></td>
                  <td style={cell}>{o.errorText || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>

        <Section title={`Transactions (${transactions.length})`} empty={transactions.length === 0 && 'No charges or refunds.'}>
          <table style={table}>
            <thead>
              <tr style={headRow}>
                <th style={cell}>Kind</th>
                <th style={cell}>Status</th>
                <th style={cell}>Player</th>
                <th style={cell}>Amount</th>
                <th style={cell}>Reference</th>
                <th style={cell}>Created / settled</th>
                <th style={cell}>Error</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((t, idx) => (
                <tr key={`${t.externalReference || idx}-${t.kind}`} style={bodyRow}>
                  <td style={cell}>{t.kind}<div style={muted}>{t.provider}</div></td>
                  <td style={cell}><StatusBadge status={t.status} /></td>
                  <td style={cell}>{user(t.userId)}</td>
                  <td style={cell}>{fmtMoney(t.amountMinor, t.currencyIsoCode)}</td>
                  <td style={cell}>
                    <code style={{ fontSize: 12 }}>{t.externalReference || '—'}</code>
                    {t.frisbiiUrl && <div><a href={t.frisbiiUrl} target='_blank' rel='noopener noreferrer' style={{ fontSize: 12 }}>Frisbii ↗</a></div>}
                  </td>
                  <td style={cell}>{fmtUtc(t.createdAtUtc)}<div style={muted}>{fmtUtc(t.settledAtUtc)}</div></td>
                  <td style={cell}>{t.errorText || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>

        <Section title='Timeline' empty={timeline.length === 0 && 'No events.'}>
          <table style={table}>
            <thead>
              <tr style={headRow}>
                <th style={cell}>When</th>
                <th style={cell}>Event</th>
                <th style={cell}>By</th>
                <th style={cell}>Details</th>
              </tr>
            </thead>
            <tbody>
              {timeline.map((e, idx) => (
                <tr key={`${e.atUtc}-${idx}`} style={bodyRow}>
                  <td style={cell}>{fmtUtc(e.atUtc)}</td>
                  <td style={cell}>{e.type}</td>
                  <td style={cell}>{e.actorUserId ? user(e.actorUserId) : <span style={muted}>system</span>}</td>
                  <td style={cell}>{e.payload || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      </div>
    </>
  );
};

const Field = ({ label, children }) => (
  <div>
    <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: '#999', marginBottom: 2 }}>{label}</div>
    <div>{children}</div>
  </div>
);
Field.propTypes = { label: PropTypes.string.isRequired, children: PropTypes.node };

const Section = ({ title, empty, children }) => (
  <>
    <h3 style={{ marginTop: 24, color: '#1a3559' }}>{title}</h3>
    {empty
      ? <p style={{ color: '#999' }}>{empty}</p>
      : <div style={tableWrap}>{children}</div>}
  </>
);
Section.propTypes = { title: PropTypes.string.isRequired, empty: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]), children: PropTypes.node };

PlayGameInternal.propTypes = {
  api: PropTypes.object.isRequired,
  match: PropTypes.object,
  params: PropTypes.object,
};

export default connect(() => ({}))(withRouter(withApi(PlayGameInternal)));
